// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {useMemo} from 'react';

import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {PANEL_BANK_EVALUATION_NAME} from '../../../util/panelbankTypes';
import {
  useRampFlagMetricsSearchAPI,
  useRampFlagWeaveInWorkspace,
  useStatsigGateMultiMetricQuickAdd,
  useStatsigGateWorkspaceConsolidation,
} from '../../../util/rampFeatureFlags';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {useQuickAddKeys} from '../PanelsQuickAdd/hooks/useQuickAddKeys';
import {useWorkspaceSettingsContext} from '../Settings/WorkspaceSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {WorkspaceDrawerScroller} from '../WorkspaceDrawerScroller';
import {AddPanelsMenuGroup} from './AddPanelsMenuGroup';
import {
  ADD_PANELS_MENU_GROUPS,
  ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD,
  EVALUATION_ITEMS,
  WEAVE_ITEMS,
} from './constants';
import {useDefaultSectionRefs} from './hooks/useDefaultSectionRefs';
import {useIsPanelTypeEnabled} from './hooks/useIsPanelTypeEnabled';

export const AddPanels = () => {
  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  const {closeWorkspaceDrawer, targetSectionRef: drawerSectionRef} =
    useWorkspaceDrawerContext();
  const {isSingleRun} = useSingleRunModeContext();
  const isPanelTypeEnabled = useIsPanelTypeEnabled();

  const {workspaceLayout} = useWorkspaceSettingsContext();

  // this needs to actually check against false because undefined is auto mode
  const isManualMode = workspaceLayout.shouldAutoGeneratePanels === false;

  const {entityName} = useProjectParams();

  const useMetricsSearchAPI = useRampFlagMetricsSearchAPI(entityName);

  const {keysWithNoPanels} = useQuickAddKeys();

  const isWeaveInWorkspaceEnabled = useRampFlagWeaveInWorkspace();
  const isMultiMetricQuickAddEnabled = useStatsigGateMultiMetricQuickAdd();

  const isQuickAddShown =
    isMultiMetricQuickAddEnabled ||
    keysWithNoPanels.length > 0 ||
    (isManualMode && useMetricsSearchAPI);

  const menuGroups = useMemo(() => {
    const menuGroups = isQuickAddShown
      ? ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD
      : ADD_PANELS_MENU_GROUPS;

    if (!isWeaveInWorkspaceEnabled) {
      return menuGroups;
    }

    // redefine `const menuGroups` by adding WEAVE_ITEMS to the EVALUATION_ITEMS
    return menuGroups.map(group => {
      if (group.title !== PANEL_BANK_EVALUATION_NAME) {
        return group;
      }
      return {...group, items: [...EVALUATION_ITEMS, ...WEAVE_ITEMS]};
    });
  }, [isQuickAddShown, isWeaveInWorkspaceEnabled]);

  // perf: calling these hooks here instead of for each AddPanelsMenuGroup/AddPanelsMenuItem
  // because they could be expensive for workspaces with lots of sections/panels
  const defaultSectionRefs = useDefaultSectionRefs(menuGroups);

  return (
    <>
      <div className="flex items-center justify-between border-b border-solid border-moon-250 px-24 py-12">
        <h2 className="text-xl font-semibold leading-[40px]">Add panels</h2>
        <Button
          variant="ghost"
          icon="close"
          onClick={() => closeWorkspaceDrawer()}
        />
      </div>
      <WorkspaceDrawerScroller role="menu">
        {menuGroups.map((group, idx) => {
          const filteredItems =
            isSingleRun && !isWorkspaceConsolidationEnabled
              ? group.items.filter(item => !item.isMultiRunOnly)
              : group.items;

          if (!filteredItems.length) {
            return null;
          }

          const defaultSectionRef = group.title
            ? defaultSectionRefs?.get(group.title)
            : undefined;
          const sectionRef = drawerSectionRef ?? defaultSectionRef;
          const itemsWithDisableState = filteredItems.map(item => ({
            ...item,
            isDisabled: !isPanelTypeEnabled(item.type),
          }));

          return (
            <React.Fragment key={idx}>
              {idx !== 0 && (
                <div role="separator" className="h-px bg-moon-250" />
              )}
              <AddPanelsMenuGroup
                {...group}
                sectionRef={sectionRef}
                items={itemsWithDisableState}
              />
            </React.Fragment>
          );
        })}
      </WorkspaceDrawerScroller>
    </>
  );
};
